import React, { FC, useMemo } from 'react';
import Config from 'react-native-config';
import styled from 'styled-components/native';

import { EDataTestId } from '@webtrader/core/constants/test';
import dynamicSize from '@webtrader/core/dynamic-size';
import { TLang, changeLanguage, useTranslation } from '@webtrader/core/i18n';
import { useCompanyArabicLanguageAvailable, useCompanyLanguageOptions } from '@webtrader/core/services/company';

import ActionSheet from '@/components/ActionSheet';
import { ISelectOption } from '@/components/Select';

type LanguageSelectOption = ISelectOption<TLang>;

const LanguageDropdown: FC = () => {
  const {
    i18n: { language }
  } = useTranslation();

  const isArabicLanguageAvailable = useCompanyArabicLanguageAvailable(Config?.ENVIRONMENT);
  const defaultLanguageOptions = useCompanyLanguageOptions();

  const options = useMemo<LanguageSelectOption[]>(() => {
    let langOptions = defaultLanguageOptions.map((lang) => ({
      label: lang.label,
      value: lang.value as TLang,
      testID: EDataTestId.LANGUAGE_OPTION
    }));

    // Only show Arabic language for SIT tenants and AQX trader (UAT)
    if (!isArabicLanguageAvailable) {
      langOptions = langOptions.filter((option) => option.value !== 'ar_AE');
    }

    return langOptions;
  }, [defaultLanguageOptions, isArabicLanguageAvailable]);

  const onChangeLanguage = async (language: TLang) => {
    await changeLanguage({ language });
  };

  return (
    <Container>
      <ActionSheet
        size="small"
        value={language as TLang}
        options={options}
        withBorder={false}
        testID={EDataTestId.LANGUAGE_DROPDOWN}
        onChange={onChangeLanguage}
      />
    </Container>
  );
};

export default LanguageDropdown;

const Container = styled.View`
  align-self: flex-end;
  margin-bottom: ${dynamicSize.convertSize(16)}px;
`;
